import CloseIcon from '@mui/icons-material/Close';
import PostAddIcon from '@mui/icons-material/PostAdd';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { LoadingButton } from '@mui/lab';
import {
    Box,
    Button,
    Card,
    CardContent,
    Drawer,
    IconButton,
    Step,
    StepLabel,
    Stepper,
    Typography
} from '@mui/material';
import { Notify } from 'notiflix';
import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import { makeNewAgreement } from '../../../../data/api/agreements/AgreementRoute';
import { getAgreementTemplate, getAgreementTemplates } from '../../../../data/api/agreements/AgreementTemplateRoute';
import useIndex from '../../../../hooks/useIndex';
import validErrors from '../../../../hooks/validErrors';
import Step1Parties from './Step1Parties';
import Step2Commercial from './Step2Commercial';
import Step3Content from './Step3Content';
import Step4Review from './Step4Review';
import applyPlaceholders from './applyPlaceholders';

const STEPS = ['Partes', 'Términos comerciales', 'Contenido', 'Revisión'];

const AgreementWizard = ({ user, loadData }) => {
    const { token, auth } = useIndex();
    const isAdmin = String(auth?.role_id) === String(process.env.REACT_APP_ADMIN_ROLE);

    const [open, setOpen] = useState(false);
    const [activeStep, setActiveStep] = useState(0);
    const [submitting, setSubmitting] = useState(false);
    const [templates, setTemplates] = useState([]);
    const [selectedTemplateId, setSelectedTemplateId] = useState(null);
    const [selectedCompany, setSelectedCompany] = useState(null);

    const initialCounterparty = isAdmin ? null : user;

    const form = useForm({
        defaultValues: buildDefaultValues(initialCounterparty),
    });

    const {
        control,
        register,
        handleSubmit,
        reset,
        trigger,
        watch,
        setValue,
        getValues,
        formState: { errors }
    } = form;

    useEffect(() => {
        if (open) {
            loadTemplates();
        } else {
            reset(buildDefaultValues(initialCounterparty));
            setActiveStep(0);
            setSelectedTemplateId(null);
            setSelectedCompany(null);
        }
    }, [open]);

    const loadTemplates = async () => {
        try {
            const { data } = await getAgreementTemplates(token);
            if (data?.success) setTemplates(data?.data ?? []);
        } catch (error) {
            validErrors(error);
        }
    };

    const selectedTemplate = useMemo(
        () => templates.find((t) => t.id === selectedTemplateId) ?? null,
        [templates, selectedTemplateId]
    );

    const requiresMoves = selectedTemplate?.requires_moves !== false;

    const handleTemplateChange = async (templateId) => {
        setSelectedTemplateId(templateId);

        if (!templateId) {
            setValue('template_id', null);
            return;
        }

        const tpl = templates.find((t) => t.id === templateId);
        if (!tpl?.uuid) return;

        try {
            const { data } = await getAgreementTemplate(token, tpl.uuid);

            if (data?.success && data?.data) {
                const t = data.data;
                const ctx = getValues();

                setValue('template_id', t.id);
                setValue('agreement_type', t.agreement_type ?? '');
                setValue('title', applyPlaceholders(t.title_template ?? '', ctx));
                setValue('description', applyPlaceholders(t.description_template ?? '', ctx));
                setValue('terms_conditions', applyPlaceholders(t.terms_conditions_template ?? '', ctx));
                setValue('special_clauses', applyPlaceholders(t.special_clauses_template ?? '', ctx));
            }
        } catch (error) {
            validErrors(error);
        }
    };

    const handleCompanySelect = (company) => {
        setSelectedCompany(company);

        if (!company) {
            setValue('company_name', '');
            setValue('company_email', '');
            setValue('company_phone', '');
            setValue('company_document', '');
            setValue('document_type_id', '');
            return;
        }

        setValue('company_name', company.name ?? '', { shouldValidate: true });
        setValue('company_email', company.email ?? '', { shouldValidate: true });
        setValue('company_phone', company.phone ?? '', { shouldValidate: true });
        setValue('company_document', company.document ?? '', { shouldValidate: true });
        setValue('document_type_id', company.document_type_id ?? '', { shouldValidate: true });

        if (selectedTemplateId) {
            handleTemplateChange(selectedTemplateId);
        }
    };

    const handleNext = async () => {
        const fields = fieldsByStep(activeStep, requiresMoves);
        const ok = fields.length === 0 ? true : await trigger(fields);

        if (!ok) return;

        setActiveStep((s) => Math.min(s + 1, STEPS.length - 1));
    };

    const handleBack = () => {
        setActiveStep((s) => Math.max(s - 1, 0));
    };

    const onSubmit = async (raw) => {
        const counterpartyId = isAdmin ? selectedCompany?.id : user?.id;

        if (!counterpartyId) {
            Notify.failure('Selecciona la empresa contratante antes de crear el contrato.');
            setActiveStep(0);
            return;
        }

        setSubmitting(true);

        try {
            const payload = {
                ...raw,
                user_id: counterpartyId,
                gift_moves: raw.gift_moves != null && raw.gift_moves !== '' ? Number(raw.gift_moves) : null,
                moves_amount: raw.moves_amount != null && raw.moves_amount !== '' ? Number(raw.moves_amount) : null,
                agreement_amount: raw.agreement_amount != null && raw.agreement_amount !== '' ? Number(raw.agreement_amount) : null,
            };

            const { data } = await makeNewAgreement(token, payload);

            if (data?.success) {
                Notify.success(data?.message ?? 'Contrato creado.');
                setOpen(false);
                loadData?.();
            }
        } catch (error) {
            validErrors(error);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <Button
                variant="contained"
                startIcon={<PostAddIcon />}
                onClick={() => setOpen(true)}
                sx={primaryButtonStyles}
            >
                Crear contrato
            </Button>

            <Drawer
                anchor="right"
                open={open}
                onClose={() => !submitting && setOpen(false)}
                PaperProps={{
                    sx: {
                        width: { xs: '100%', sm: '92%', md: '80%', lg: '70%' },
                        maxWidth: 1100,
                        background: '#f5f7fb',
                    },
                }}
            >
                <DrawerLayout>
                    <DrawerHeader>
                        <Box>
                            <Typography className="title">
                                Nuevo contrato
                            </Typography>

                            <Typography className="subtitle">
                                Completa la información paso a paso para crear el contrato.
                            </Typography>
                        </Box>

                        <IconButton
                            size="small"
                            onClick={() => !submitting && setOpen(false)}
                            sx={{
                                color: '#10285f',
                                background: 'rgba(16, 40, 95, 0.06)',
                                '&:hover': {
                                    background: 'rgba(214, 170, 58, 0.16)',
                                },
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </DrawerHeader>

                    <DrawerContent>
                        <StepperCard>
                            <Stepper
                                activeStep={activeStep}
                                alternativeLabel
                                sx={{
                                    '& .MuiStepLabel-label': {
                                        fontWeight: 800,
                                        color: 'rgba(15, 34, 77, 0.55)',
                                        fontSize: { xs: '0.68rem', md: '0.82rem' },
                                    },
                                    '& .MuiStepLabel-label.Mui-active': {
                                        color: '#10285f',
                                        fontWeight: 900,
                                    },
                                    '& .MuiStepLabel-label.Mui-completed': {
                                        color: '#10285f',
                                    },
                                    '& .MuiStepIcon-root': {
                                        color: 'rgba(15, 34, 77, 0.18)',
                                    },
                                    '& .MuiStepIcon-root.Mui-active': {
                                        color: '#d6aa3a',
                                    },
                                    '& .MuiStepIcon-root.Mui-completed': {
                                        color: '#10285f',
                                    },
                                }}
                            >
                                {STEPS.map((label) => (
                                    <Step key={label}>
                                        <StepLabel>{label}</StepLabel>
                                    </Step>
                                ))}
                            </Stepper>
                        </StepperCard>

                        <WizardCard variant="outlined">
                            <CardContent>
                                <form
                                    onSubmit={handleSubmit(onSubmit)}
                                    noValidate
                                    id="agreement-wizard-form"
                                >
                                    {activeStep === 0 && (
                                        <Step1Parties
                                            control={control}
                                            register={register}
                                            errors={errors}
                                            templates={templates}
                                            onTemplateChange={handleTemplateChange}
                                            selectedTemplateId={selectedTemplateId}
                                            isAdmin={isAdmin}
                                            selectedCompanyId={selectedCompany?.id ?? null}
                                            onCompanySelect={handleCompanySelect}
                                        />
                                    )}

                                    {activeStep === 1 && (
                                        <Step2Commercial
                                            control={control}
                                            register={register}
                                            watch={watch}
                                            setValue={setValue}
                                            errors={errors}
                                            requiresMovesDefault={requiresMoves}
                                        />
                                    )}

                                    {activeStep === 2 && (
                                        <Step3Content
                                            control={control}
                                            register={register}
                                            errors={errors}
                                        />
                                    )}

                                    {activeStep === 3 && (
                                        <Step4Review formValues={getValues()} />
                                    )}
                                </form>
                            </CardContent>
                        </WizardCard>
                    </DrawerContent>

                    <DrawerFooter>
                        <Button
                            disabled={activeStep === 0 || submitting}
                            onClick={handleBack}
                            startIcon={<ArrowBackIcon />}
                            sx={secondaryButtonStyles}
                        >
                            Atrás
                        </Button>

                        <StepCounter>
                            Paso <strong>{activeStep + 1}</strong> de {STEPS.length}
                        </StepCounter>

                        {activeStep < STEPS.length - 1 ? (
                            <Button
                                variant="contained"
                                onClick={handleNext}
                                endIcon={<ArrowForwardIcon />}
                                sx={primaryButtonStyles}
                            >
                                Siguiente
                            </Button>
                        ) : (
                            <LoadingButton
                                type="submit"
                                form="agreement-wizard-form"
                                variant="contained"
                                loading={submitting}
                                startIcon={<PostAddIcon />}
                                sx={primaryButtonStyles}
                            >
                                <span>Crear contrato</span>
                            </LoadingButton>
                        )}
                    </DrawerFooter>
                </DrawerLayout>
            </Drawer>
        </>
    );
};

const buildDefaultValues = (user) => ({
    fbc_name: 'Fenabocol',
    fbc_address: 'Calle 43a sur #78g - 28 Bogotá',
    fbc_email: 'fenabocolorg@gmail.com',
    fbc_phone: '3002910614',

    company_name: user?.name ?? '',
    company_address: '',
    company_email: user?.email ?? '',
    company_phone: user?.phone ?? '',
    document_type_id: user?.document_type_id ?? '',
    company_document: user?.document ?? '',
    signatory_name: '',
    signatory_position: '',

    agreement_type: 'Contrato de Venta de Productos Digitales',
    currency: 'COP',
    moves_amount: 1,
    agreement_amount: 1000,
    gift_moves: null,
    income_types_id: '',
    payment_type_desc: '',
    days_to_pay: 1,
    days_breach: 1,
    days_rescission: 1,

    title: 'CONTRATO DE VENTA Y DISTRIBUCIÓN DE CÓDIGOS PARA PARTICIPACIÓN EN BINGO FENABOCOL',
    description: '',
    terms_conditions: '',
    special_clauses: '',

    template_id: null,
});

const fieldsByStep = (stepIndex, requiresMoves) => {
    switch (stepIndex) {
        case 0:
            return [
                'fbc_name',
                'fbc_address',
                'fbc_email',
                'fbc_phone',
                'company_name',
                'company_address',
                'company_email',
                'company_phone',
                'document_type_id',
                'company_document',
                'signatory_name',
                'signatory_position',
            ];

        case 1:
            if (!requiresMoves) return ['agreement_type'];

            return [
                'agreement_type',
                'currency',
                'moves_amount',
                'agreement_amount',
                'income_types_id',
                'days_to_pay',
                'days_breach',
                'days_rescission',
            ];

        case 2:
            return ['title', 'description', 'terms_conditions'];

        default:
            return [];
    }
};

export default AgreementWizard;

const primaryButtonStyles = {
    borderRadius: '14px',
    fontWeight: 900,
    textTransform: 'uppercase',
    background: 'linear-gradient(90deg, #10285f 0%, #1a2f5c 100%)',
    boxShadow: '0 6px 14px rgba(15, 34, 77, 0.18)',
    color: '#fff',
    '&:hover': {
        background: 'linear-gradient(90deg, #1a2f5c 0%, #10285f 100%)',
    },
    '&.Mui-disabled': {
        background: 'rgba(15, 34, 77, 0.12)',
        color: 'rgba(15, 34, 77, 0.36)',
        boxShadow: 'none',
    },
};

const secondaryButtonStyles = {
    borderRadius: '14px',
    fontWeight: 900,
    textTransform: 'uppercase',
    color: '#10285f',
    border: '1px solid rgba(214, 170, 58, 0.65)',
    background: '#fff',
    px: 2,
    '&:hover': {
        background: 'rgba(214, 170, 58, 0.12)',
        borderColor: '#d6aa3a',
    },
    '&.Mui-disabled': {
        color: 'rgba(15, 34, 77, 0.3)',
        borderColor: 'rgba(15, 34, 77, 0.08)',
    },
};

const DrawerLayout = styled(Box)`
    display: flex;
    flex-direction: column;
    height: 100%;
    background: #f5f7fb;
`;

const DrawerHeader = styled(Box)`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
    padding: 1rem 1.5rem;
    border-bottom: 1px solid rgba(15, 34, 77, 0.1);
    background: linear-gradient(180deg, #ffffff 0%, #f6f8ff 100%);

    .title {
        color: #10285f;
        font-size: 1.35rem;
        font-weight: 900;
    }

    .subtitle {
        margin-top: 0.15rem;
        color: rgba(15, 34, 77, 0.62);
        font-size: 0.88rem;
        font-weight: 600;
    }

    @media (max-width: 600px) {
        padding: 0.9rem 1rem;

        .title {
            font-size: 1.12rem;
        }

        .subtitle {
            font-size: 0.78rem;
        }
    }
`;

const DrawerContent = styled(Box)`
    flex: 1;
    overflow-y: auto;
    padding: 1.4rem;

    @media (max-width: 600px) {
        padding: 1rem;
    }
`;

const StepperCard = styled.div`
    padding: 1rem;
    margin-bottom: 1rem;
    border-radius: 20px;
    border: 1px solid rgba(214, 170, 58, 0.24);
    background: linear-gradient(180deg, #ffffff 0%, #f8faff 100%);
    box-shadow: 0 10px 22px rgba(15, 34, 77, 0.06);

    @media (max-width: 600px) {
        padding: 0.8rem 0.4rem;
        border-radius: 16px;
    }
`;

const WizardCard = styled(Card)`
    border-radius: 22px !important;
    border: 1px solid rgba(15, 34, 77, 0.1) !important;
    background: linear-gradient(180deg, #ffffff 0%, #f8faff 100%) !important;
    box-shadow: 0 12px 28px rgba(15, 34, 77, 0.08) !important;
    overflow: visible !important;
`;

const DrawerFooter = styled(Box)`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
    padding: 1rem 1.5rem;
    border-top: 1px solid rgba(15, 34, 77, 0.1);
    background: linear-gradient(180deg, #ffffff 0%, #f6f8ff 100%);

    @media (max-width: 600px) {
        padding: 0.85rem 1rem;
        gap: 0.5rem;

        button {
            font-size: 0.72rem;
            padding-left: 0.75rem;
            padding-right: 0.75rem;
        }
    }
`;

const StepCounter = styled(Typography)`
    color: rgba(15, 34, 77, 0.62);
    font-size: 0.82rem !important;
    font-weight: 700 !important;
    white-space: nowrap;

    strong {
        color: #10285f;
        font-weight: 900;
    }

    @media (max-width: 500px) {
        display: none;
    }
`;